'use client';

import { useRef, useEffect } from 'react';
import { usePathname, useParams } from 'next/navigation';
import PropTypes from 'prop-types';
import gsap from 'gsap';
import { CustomEase } from 'gsap/all';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollSmoother } from 'gsap/ScrollSmoother';
import { useUi } from '@weareconceptstudio/core';
import { setSmoother } from './gsapSmootherInstance';
import { useDeviceDetection } from './useDeviceDetection';
import ScrollWrapperErrorBoundary from './ErrorBoundary';

gsap.registerPlugin(ScrollTrigger, ScrollSmoother, CustomEase);

function ScrollWrapper({ children, isEnabled = true }) {
	const { isBurgerMenuOpen, popupIsOpen } = useUi();
	const { isDesktop, hasTouchScreen } = useDeviceDetection();

	//! Refs
	const main = useRef(null);
	const smoother = useRef(null);

	//! Next Navigation
	const pathname = usePathname();
	const params = useParams();

	useEffect(() => {
		if (smoother.current !== undefined && smoother.current !== null) {
			gsap.to(smoother.current, {
				scrollTop: 0,
				duration: 0,
			});
		}
		// //! searchparams not dynamic
	}, [pathname, params.lang]);
	useGSAP(
		() => {
			// Only create smoother if enabled and conditions are met
			if (!isEnabled || !isDesktop || hasTouchScreen) {
				return;
			}

			try {
				smoother.current = ScrollSmoother.create({
					smooth: 0.7,
					speed: 1,
					ease: CustomEase.create('custom', 'M0,0 C0.11,0.354 0.134,0.963 1,1 '),
					normalizeScroll: {
						lockAxis: false,
						allowNestedScroll: true,
						type: 'touch,wheel,pointer',
					},
				});
				setSmoother(smoother.current);
			} catch (error) {
				console.warn('Failed to create ScrollSmoother:', error);
			}
		},
		{
			scope: main,
			dependencies: [pathname, isEnabled, isDesktop, hasTouchScreen],
			revertOnUpdate: true, // Clean up previous instance on update
		}
	);

	//! Cleanup smoother on unmount
	useGSAP(
		() => {
			return () => {
				if (smoother.current) {
					smoother.current.kill();
					smoother.current = null;
				}
			};
		},
		{ scope: main }
	);

	useEffect(() => {
		if (smoother.current) {
			smoother.current.paused(isBurgerMenuOpen || popupIsOpen, true);
		}
	}, [isBurgerMenuOpen, popupIsOpen]);

	return (
		<ScrollWrapperErrorBoundary>
			<div
				ref={main}
				id='smooth-wrapper'>
				<div id='smooth-content'>{children}</div>
			</div>
		</ScrollWrapperErrorBoundary>
	);
}

ScrollWrapper.propTypes = {
	children: PropTypes.node.isRequired,
	isEnabled: PropTypes.bool,
};

export default ScrollWrapper;
